import { useEffect, useRef } from 'react';
import { Label, Select, SearchableSelect } from '@shared/components';
import { invoicingDealerControl, isSoleOptionAnnouncement } from '../utils/invoicingDealerControl';

export const INVOICING_DEALER_GATED_NOTE = 'You do not have permission to see the dealers you can invoice for.';

interface Props<TOption> {
    id: string;
    /** The caller owns the single-option default; the field never selects a sole dealer itself. */
    options: TOption[];
    value: TOption | null;
    onChange: (next: TOption | null) => void;
    getOptionValue: (option: TOption) => string;
    getOptionLabel: (option: TOption) => string;
    loading?: boolean;
    disabled?: boolean;
    emptyLabel: string;
    error?: string;
    note?: string | null;
}

interface ControlProps<TOption> extends Omit<Props<TOption>, 'note' | 'error'> {
    labelId: string;
    errorId: string;
    hasError: boolean;
}

function PlainDealerSelect<TOption>({
    id,
    labelId,
    errorId,
    hasError,
    options,
    value,
    onChange,
    getOptionValue,
    getOptionLabel,
    loading,
    disabled,
}: ControlProps<TOption>) {
    const selectOptions = options.map((option) => ({ value: getOptionValue(option), label: getOptionLabel(option) }));
    const awaitingDefault = isSoleOptionAnnouncement(options.length, value !== null);
    const droppedRef = useRef<string | null>(null);

    useEffect(() => {
        if (!awaitingDefault) droppedRef.current = null;
    }, [awaitingDefault]);

    const handleChange = (next: string) => {
        if (disabled) return;
        if (awaitingDefault && droppedRef.current !== next) {
            droppedRef.current = next;
            return;
        }
        onChange(options.find((option) => getOptionValue(option) === next) ?? null);
    };

    return (
        <Select
            id={id}
            options={selectOptions}
            value={value === null ? '' : getOptionValue(value)}
            onChange={handleChange}
            placeholder="Select a dealer"
            isLoading={loading}
            disabled={disabled}
            error={hasError}
            ariaLabelledBy={labelId}
            ariaDescribedBy={hasError ? errorId : undefined}
        />
    );
}

function SearchDealerSelect<TOption>({
    id,
    errorId,
    hasError,
    options,
    value,
    onChange,
    getOptionValue,
    getOptionLabel,
    loading,
    disabled,
    emptyLabel,
}: ControlProps<TOption>) {
    return (
        <SearchableSelect<TOption>
            id={id}
            options={options}
            value={value}
            onChange={onChange}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            loading={loading}
            disabled={disabled}
            placeholder="Search dealers..."
            emptyLabel={emptyLabel}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorId : undefined}
        />
    );
}

export function InvoicingDealerField<TOption>({ error, note = null, ...control }: Props<TOption>) {
    const { id, options, loading = false, disabled = false } = control;
    const errorId = `${id}-error`;
    const labelId = `${id}-label`;
    const searching = invoicingDealerControl(options.length) === 'search';
    const controlProps = { ...control, loading, disabled, labelId, errorId, hasError: !!error };

    return (
        <div>
            <Label id={labelId} htmlFor={searching ? id : undefined} required>
                Invoicing dealer
            </Label>
            {searching ? <SearchDealerSelect {...controlProps} /> : <PlainDealerSelect {...controlProps} />}
            {error && (
                <p id={errorId} className="mt-1.5 text-xs text-mp-error">
                    {error}
                </p>
            )}
            {note && <p className="mt-1.5 text-xs text-mp-muted">{note}</p>}
        </div>
    );
}
